/**
 * "Link this browser" — the page Chrome's `identity.launchWebAuthFlow` opens.
 *
 * The Worker has already checked the return address and that someone is signed
 * in before serving this page; what is left is a decision and a name for the
 * browser. The decision is posted as a REAL form to `POST /link/approve`,
 * not through `fetch`: the answer is a 302 to a `chromiumapp.org` URL that
 * Chrome intercepts, and only a navigation gives Chrome that redirect. The
 * token arrives in the fragment, so it never reaches a server log.
 */

import { useMemo, useState, type ReactNode } from "react";
import Alert from "react-bootstrap/Alert";
import Button from "react-bootstrap/Button";
import Card from "react-bootstrap/Card";
import Container from "react-bootstrap/Container";
import Form from "react-bootstrap/Form";
import { useSearchParams } from "react-router";
import { SkeletonRows } from "../components/ui";
import { useSession } from "../useSession";

/** A name someone will recognise in a list six months from now. */
function defaultDeviceName(userAgent: string): string {
  const browser = /\bEdg\//.test(userAgent)
    ? "Edge"
    : /\bOPR\//.test(userAgent)
      ? "Opera"
      : /\bBrave\//.test(userAgent)
        ? "Brave"
        : /\bChrome\//.test(userAgent)
          ? "Chrome"
          : "Browser";
  const platform = /Windows/.test(userAgent)
    ? "Windows"
    : /Mac OS X|Macintosh/.test(userAgent)
      ? "macOS"
      : /CrOS/.test(userAgent)
        ? "ChromeOS"
        : /Android/.test(userAgent)
          ? "Android"
          : /Linux/.test(userAgent)
            ? "Linux"
            : "";
  return platform ? `${browser} on ${platform}` : browser;
}

export function LinkBrowser(): ReactNode {
  const [params] = useSearchParams();
  const { account, loading } = useSession();
  const redirectUri = params.get("redirect_uri") ?? "";
  const state = params.get("state");
  const [name, setName] = useState(() => defaultDeviceName(navigator.userAgent));
  const extensionId = useMemo(() => /^https:\/\/([a-p]{32})\.chromiumapp\.org\//.exec(redirectUri)?.[1] ?? null, [redirectUri]);

  if (!redirectUri) {
    return (
      <Container style={{ maxWidth: "34rem" }}>
        <Card>
          <Card.Body className="p-4">
            <h1 className="h5 mb-2">Start this from the extension</h1>
            <p className="emb-muted mb-0">
              Linking begins in the xtract.bot Embellisher extension — open its side panel and choose{" "}
              <strong>Link this browser</strong>. This page needs the return address the extension supplies, and there
              is none in this URL.
            </p>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  return (
    <Container style={{ maxWidth: "36rem" }}>
      <Card>
        <Card.Body className="p-4 p-md-5">
          <p className="emb-eyebrow mb-2">Link a browser</p>
          <h1 className="h4 mb-3">Link this browser to your account?</h1>

          {loading ? (
            <SkeletonRows rows={4} />
          ) : !account ? (
            <Alert variant="warning" className="mb-0">
              Your session ended before you answered.{" "}
              <Alert.Link href={`/signin?next=${encodeURIComponent(`${window.location.pathname}${window.location.search}`)}`}>
                Sign in again
              </Alert.Link>{" "}
              and the extension will pick up where it left off.
            </Alert>
          ) : (
            <Form method="post" action="/link/approve">
              <input type="hidden" name="redirect_uri" value={redirectUri} />
              {state === null ? null : <input type="hidden" name="state" value={state} />}

              <div className="emb-sunken p-3 mb-4">
                <div className="small emb-muted">Signed in as</div>
                <div className="fw-semibold">{account.username}</div>
                <div className="emb-muted small">{account.email ?? "no verified email"} · {account.provider}</div>
              </div>

              <Form.Group className="mb-4" controlId="emb-device-name">
                <Form.Label>Name this browser</Form.Label>
                <Form.Control
                  name="name"
                  value={name}
                  maxLength={80}
                  onChange={(event) => setName(event.target.value)}
                  autoComplete="off"
                />
                <Form.Text className="emb-muted">
                  Only you see it. It is how you tell this browser from your others when revoking one later.
                </Form.Text>
              </Form.Group>

              <div className="emb-accent-panel p-3 mb-4">
                <p className="fw-semibold small mb-2">Linking lets an assistant you connect, on request:</p>
                <ul className="small mb-2 ps-3 d-flex flex-column gap-1">
                  <li>read the tab this browser has open — its structure, text and screenshots — and list open tabs;</li>
                  <li>show overlays and menu items in your pages, and run the features you have configured;</li>
                  <li>change this extension's configuration and run logic modules in it.</li>
                </ul>
                <p className="emb-muted small mb-0">
                  Nothing happens on its own: every one of those is a tool call your assistant makes, recorded and shown
                  live in the extension's Activity view. You can revoke this link at any time from the Account page.
                </p>
              </div>

              {extensionId ? (
                <p className="emb-muted small">
                  Returning to extension <code className="emb-mono">{extensionId}</code>.
                </p>
              ) : (
                <p className="emb-muted small">
                  Returning to <code className="emb-url">{redirectUri}</code>.
                </p>
              )}

              <div className="d-flex gap-2">
                <Button type="submit" name="decision" value="approve" variant="primary" className="flex-grow-1">
                  Link this browser
                </Button>
                <Button type="submit" name="decision" value="cancel" variant="light">
                  Cancel
                </Button>
              </div>
            </Form>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
}
